import type { BuildGuideStepProgress, BuildProgress } from "./getProjectBuildGuide";

const SUBSTEP_ID_SUFFIX = "--substep-";

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

function normalizeStringList(value: unknown): string[] {
	if (!Array.isArray(value)) return [];

	const seen = new Set<string>();
	const next: string[] = [];

	for (const candidate of value) {
		if (typeof candidate !== "string" || candidate.length === 0) continue;
		if (seen.has(candidate)) continue;
		seen.add(candidate);
		next.push(candidate);
	}

	return next;
}

export function createBuildGuideSubstepId(
	stepId: string,
	index: number,
): string {
	return `${stepId}${SUBSTEP_ID_SUFFIX}${String(index + 1).padStart(2, "0")}`;
}

export function isBuildGuideSubstepId(value: string): boolean {
	return value.includes(SUBSTEP_ID_SUFFIX);
}

export function normalizeBuildGuideStepProgress(
	value: unknown,
): BuildGuideStepProgress {
	if (!isRecord(value)) {
		return {
			completed: false,
			completedCheckpointIds: [],
			completedSubstepIds: [],
		};
	}

	const rawCheckpointIds = normalizeStringList(value.completedCheckpointIds);
	const completedSubstepIds = normalizeStringList(value.completedSubstepIds);
	const checkpointSubstepIds = rawCheckpointIds.filter(isBuildGuideSubstepId);
	const completedCheckpointIds = rawCheckpointIds.filter(
		(id) => !isBuildGuideSubstepId(id),
	);

	return {
		completed: value.completed === true,
		completedCheckpointIds,
		completedSubstepIds: normalizeStringList([
			...completedSubstepIds,
			...checkpointSubstepIds,
		]),
	};
}

export function normalizeBuildProgress(value: unknown): BuildProgress {
	if (!isRecord(value)) return {};

	const normalized: BuildProgress = {};

	for (const [stepId, stepProgress] of Object.entries(value)) {
		if (typeof stepId !== "string" || stepId.length === 0) continue;
		normalized[stepId] = normalizeBuildGuideStepProgress(stepProgress);
	}

	return normalized;
}

function mergeStepProgress(
	base: BuildGuideStepProgress,
	incoming: BuildGuideStepProgress,
): BuildGuideStepProgress {
	return {
		completed: base.completed || incoming.completed,
		completedCheckpointIds: normalizeStringList([
			...base.completedCheckpointIds,
			...incoming.completedCheckpointIds,
		]),
		completedSubstepIds: normalizeStringList([
			...(base.completedSubstepIds ?? []),
			...(incoming.completedSubstepIds ?? []),
		]),
	};
}

/**
 * Non-destructive union merge of two normalized progress maps. Per step,
 * completed-ids (checkpoints + substeps) are unioned and `completed` is OR'd.
 * Neither side is dropped, so merging device-local anonymous progress into a
 * server row can only add completions, never erase them. Both inputs are
 * normalized first so legacy/mixed-shape rows merge safely.
 */
export function mergeBuildProgress(
	base: unknown,
	incoming: unknown,
): BuildProgress {
	const normalizedBase = normalizeBuildProgress(base);
	const normalizedIncoming = normalizeBuildProgress(incoming);
	const merged: BuildProgress = { ...normalizedBase };

	for (const [stepId, incomingStep] of Object.entries(normalizedIncoming)) {
		const baseStep = merged[stepId];
		merged[stepId] = baseStep
			? mergeStepProgress(baseStep, incomingStep)
			: incomingStep;
	}

	return merged;
}
